import {
  type AnonymousEcc as _AnonymousEcc,
  AnonymousEcc as ANONYMOUS_ECC_VALUES,
  type AnonymousFormat,
  type AnonymousFrame,
  type AnonymousPayload,
  type AnonymousWifiAuth,
} from "@qrody/api-client/render";
import {
  Code2 as _Code2,
  FileDown,
  IdCard,
  Link2,
  Mail,
  MapPin,
  MessageSquare,
  Type,
  Wifi,
  type LucideIcon,
} from "lucide-react";
import type { PayloadType, QrGeneratorFormState } from "./seed";

// Constants ------------------------------------------------------------------

export const FORMATS: AnonymousFormat[] = ["png", "svg", "pdf"];

// SHIELD + CALL_TO_ACTION are backend-only today: the client-side preview
// renderer only paints SIMPLE and ROUNDED. Exposing the others here would
// give a preview that does not match the downloaded PNG/SVG, so they stay
// out of the public generator until the client renderer catches up.
export const FRAME_OPTIONS: AnonymousFrame[] = ["NONE", "SIMPLE", "ROUNDED"];

export const ECC_OPTIONS: readonly _AnonymousEcc[] = ANONYMOUS_ECC_VALUES;

export const WIFI_AUTHS: AnonymousWifiAuth[] = ["WPA", "WEP", "NOPASS"];

export const SIZE_STEP_PX = 50;

// Druck-DPI-Quick-Picks: rechnen Anzeigegröße (mm) × DPI / 25.4 in
// Pixel um. 100 = Bildschirm/Web/Draft, 150 = Standard-Web, 300 =
// Standard-Print, 600 = Premium / Mikrodruck (Pro-only).
export const DPI_PRESETS = [100, 150, 300, 600] as const;
export const MM_PER_INCH = 25.4;

// Untere Schranke des Resolution-Sliders: jedes QR-Modul muss bei der
// Datei-Auflösung mit min. 4 px gerendert sein, damit der Code zuverlässig
// gescannt wird ("grüner Bereich"). Hängt von Payload-Länge UND ECC ab.
export const PX_PER_MODULE_GREEN = 4;

// Lucide-Icon-Mapping pro Payload-Typ. Spiegelt die Wahl aus
// `marketing/src/components/marketing/use-cases.tsx`, damit
// Generator-Tabs und Marketing-Use-Case-Liste visuell konsistent bleiben.
export const PAYLOAD_TYPE_ICONS: Record<PayloadType, LucideIcon> = {
  url: Link2,
  text: Type,
  email: Mail,
  sms: MessageSquare,
  geo: MapPin,
  wifi: Wifi,
  vcard: IdCard,
  file: FileDown,
};

// Payload-Typen, die als Dynamic-Code im Account funktionieren — der
// Generator blendet für diese (URL/Datei) einen kontextuellen
// Account-Hinweis unter der Eingabe ein.
export const DYNAMIC_COMPATIBLE_TYPES: readonly PayloadType[] = ["url", "file"] as const;

export const isDynamicCompatible = (t: PayloadType): boolean =>
  DYNAMIC_COMPATIBLE_TYPES.includes(t);

// Store redemption links for promo/offer codes. Selecting a preset in the
// URL tab pre-fills the field with the prefix so the user only appends the
// code. The payload stays a plain `url` — no backend involvement.
// Apple: same endpoint App Store Connect uses for offer-code URLs
// (`…/redeem?ctx=offercodes&id=<app>&code=`), minus the app-specific params.
export const PROMO_CODE_PRESETS = [
  { id: "appleAppStore", urlPrefix: "https://apps.apple.com/redeem?code=" },
  { id: "googlePlay", urlPrefix: "https://play.google.com/redeem?code=" },
] as const;

export type PromoCodePresetId = (typeof PROMO_CODE_PRESETS)[number]["id"];

export function promoCodeUrl(id: PromoCodePresetId, code = ""): string {
  const preset = PROMO_CODE_PRESETS.find((p) => p.id === id);
  if (!preset) throw new Error(`Unknown promo-code preset: ${id}`);
  return preset.urlPrefix + encodeURIComponent(code.trim());
}

// Stand-in payload used by the client preview when the user hasn't filled
// in the required field yet (or has cleared it). Keeps the preview pane
// occupied so users can see colour/frame/watermark settings against a
// real-looking code instead of an empty box. Backend renders are still
// gated on a real payload (rate-limit / 400-noise).
export const PLACEHOLDER_PAYLOAD: AnonymousPayload = {
  type: "url",
  url: "https://qrody.com",
};

// Helpers --------------------------------------------------------------------

/**
 * Maps a ZodError path (e.g. `["payload", "downloadUrl"]`) onto the form
 * field id so a validation issue can be highlighted on the offending
 * Input. Same Zod key (e.g. "url") may target different fields per
 * payload type, so the mapping branches on `type`.
 */
export function zodPathToFieldId(
  type: PayloadType,
  segs: ReadonlyArray<string | number>,
): string | null {
  if (segs[0] !== "payload") return null;
  const key = String(segs[1] ?? "");
  switch (type) {
    case "url":
      return key === "url" ? "url" : null;
    case "text":
      return key === "text" ? "text" : null;
    case "email":
      if (key === "to") return "emailTo";
      if (key === "subject") return "emailSubject";
      if (key === "body") return "emailBody";
      return null;
    case "sms":
      if (key === "to") return "smsTo";
      if (key === "message") return "smsMessage";
      return null;
    case "geo":
      if (key === "latitude") return "geoLat";
      if (key === "longitude") return "geoLon";
      return null;
    case "wifi":
      if (key === "ssid") return "wifiSsid";
      if (key === "password") return "wifiPassword";
      return null;
    case "vcard":
      if (key === "fullName") return "vcardFullName";
      if (key === "email") return "vcardEmail";
      if (key === "phone") return "vcardPhone";
      if (key === "org") return "vcardOrg";
      if (key === "title") return "vcardTitle";
      if (key === "url") return "vcardUrl";
      return null;
    case "file":
      return key === "downloadUrl" ? "fileUrl" : null;
  }
}

/**
 * Build the wire-shape payload from the flat form-state. Returns null if
 * the selected type lacks required fields so the generator can skip the
 * preview render silently rather than fire a 400.
 */
export function buildPayload(
  type: PayloadType,
  f: QrGeneratorFormState,
): AnonymousPayload | null {
  switch (type) {
    case "url":
      return f.url.trim() ? { type: "url", url: f.url.trim() } : null;
    case "text":
      return f.text.trim() ? { type: "text", text: f.text } : null;
    case "email":
      return f.emailTo.trim()
        ? {
            type: "email",
            to: f.emailTo.trim(),
            subject: f.emailSubject || undefined,
            body: f.emailBody || undefined,
          }
        : null;
    case "sms":
      return f.smsTo.trim()
        ? { type: "sms", to: f.smsTo.trim(), message: f.smsMessage || undefined }
        : null;
    case "geo": {
      const lat = Number(f.geoLat);
      const lon = Number(f.geoLon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
      return { type: "geo", latitude: lat, longitude: lon };
    }
    case "wifi":
      return f.wifiSsid.trim()
        ? {
            type: "wifi",
            ssid: f.wifiSsid,
            password: f.wifiPassword || undefined,
            authentication: f.wifiAuth,
            hidden: f.wifiHidden,
          }
        : null;
    case "vcard":
      return f.vcardFullName.trim()
        ? {
            type: "vcard",
            fullName: f.vcardFullName,
            email: f.vcardEmail || undefined,
            phone: f.vcardPhone || undefined,
            org: f.vcardOrg || undefined,
            title: f.vcardTitle || undefined,
            url: f.vcardUrl || undefined,
          }
        : null;
    case "file":
      return f.fileUrl.trim() ? { type: "file", downloadUrl: f.fileUrl.trim() } : null;
  }
}
